import React, { useState, useMemo, useEffect, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDate } from '../../utils/dateFormatter';
import { Search, Building, Eye, Plus, Check, ArrowUpDown, ArrowUp, ArrowDown, ChevronLeft, ChevronRight, X, Filter, Mail } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../ui/popover';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../ui/table';
import { useClients } from '../../hooks/useClients';
import AddClientModal from './AddClientModal';
import MobileTableCard from '../ui/mobile-table-card';
import ListContainer from '../ui/list-container';

const ClientsList = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isPending, startTransition] = useTransition();
  const [addModalOpen, setAddModalOpen] = useState(false);

  // Column visibility state
  const [visibleColumns, setVisibleColumns] = useState({
    name: true,
    email: true,
    email_pref: true,
    status: true,
    rfq_count: true,
    invited_rfqs: true,
    created: true
  });

  // Email preference filter state
  const [emailPrefFilter, setEmailPrefFilter] = useState([]);

  // Sorting state - default to invited_rfqs_count descending
  const [sortField, setSortField] = useState('invited_rfqs_count');
  const [sortDirection, setSortDirection] = useState('desc');

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(15);

  // Debounce search term (non-blocking with useTransition)
  useEffect(() => {
    const timer = setTimeout(() => {
      startTransition(() => {
        setDebouncedSearchTerm(searchTerm);
      });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const { clients: allClients, loading, error, refetch } = useClients({
    search: debouncedSearchTerm
  });


  // Toggle column visibility
  const toggleColumn = (column) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [column]: !prev[column]
    }));
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Get sort icon for column headers
  const getSortIcon = (field) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 opacity-40" />;
    }
    return sortDirection === 'asc' ? (
      <ArrowUp className="h-4 w-4" />
    ) : (
      <ArrowDown className="h-4 w-4" />
    );
  };

  // Filter and sort clients
  const sortedClients = useMemo(() => {
    // First, apply email preference filter
    let filtered = allClients;
    if (emailPrefFilter.length > 0) {
      filtered = allClients.filter(client => {
        const pref = client.email_preference || 'Draft'; // Default to Draft
        return emailPrefFilter.includes(pref);
      });
    }

    const sorted = [...filtered].sort((a, b) => {
      let aValue, bValue;

      switch (sortField) {
        case 'name':
          aValue = a.name?.toLowerCase() || '';
          bValue = b.name?.toLowerCase() || '';
          break;
        case 'primary_procurement_email':
          aValue = a.primary_procurement_email?.toLowerCase() || '';
          bValue = b.primary_procurement_email?.toLowerCase() || '';
          break;
        case 'email_preference':
          aValue = a.email_preference?.toLowerCase() || '';
          bValue = b.email_preference?.toLowerCase() || '';
          break;
        case 'status':
          aValue = a.status?.toLowerCase() || '';
          bValue = b.status?.toLowerCase() || '';
          break;
        case 'rfq_count':
          aValue = a.rfq_count || 0;
          bValue = b.rfq_count || 0;
          break;
        case 'invited_rfqs_count':
          aValue = a.invited_rfqs_count || 0;
          bValue = b.invited_rfqs_count || 0;
          break;
        case 'created_at':
          aValue = new Date(a.created_at || 0);
          bValue = new Date(b.created_at || 0);
          break;
        default:
          return 0;
      }

      if (typeof aValue === 'string') {
        return sortDirection === 'asc'
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      } else {
        if (aValue < bValue) return sortDirection === 'asc' ? -1 : 1;
        if (aValue > bValue) return sortDirection === 'asc' ? 1 : -1;
        return 0;
      }
    });

    return sorted;
  }, [allClients, sortField, sortDirection, emailPrefFilter]);

  // Calculate pagination
  const totalItems = sortedClients.length;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearchTerm, itemsPerPage, emailPrefFilter]);

  // Get current page items
  const clients = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return sortedClients.slice(startIndex, endIndex);
  }, [sortedClients, currentPage, itemsPerPage]);

  // Pagination handlers
  const handlePageChange = (newPage) => {
    setCurrentPage(Math.max(1, Math.min(newPage, totalPages)));
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(Number(e.target.value));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600">Error loading clients: {error}</p>
      </div>
    );
  }

  return (
    <ListContainer>
      {/* Header */}
      <ListContainer.Header>
        <ListContainer.Title
          title="Clients"
          subtitle="Manage your client relationships"
        />
        <ListContainer.Actions>
          <Button onClick={() => setAddModalOpen(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Create
          </Button>
        </ListContainer.Actions>
      </ListContainer.Header>

      {/* Clients Table and Filters */}
      <Card>
        <CardHeader className="px-4 py-4">
          <div className="flex flex-col gap-4">
            {/* Search and Filter Controls */}
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              {/* Left side - filters */}
              <div className="flex flex-wrap gap-2">
                {/* Email Preference Filter */}
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" size="sm" className="h-9">
                      <Mail className="mr-2 h-4 w-4" />
                      Email Pref
                      {emailPrefFilter.length > 0 && (
                        <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                          {emailPrefFilter.length}
                        </Badge>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-48 p-2" align="start">
                    <div className="space-y-1">
                      <div className="text-sm font-medium mb-2 px-2">Email Preference</div>
                      {['Draft', 'Send', 'No_email'].map((pref) => {
                        const isSelected = emailPrefFilter.includes(pref);
                        const displayLabel = pref === 'No_email' ? 'No Email' : pref;
                        return (
                          <div
                            key={pref}
                            className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                            onClick={() => {
                              if (isSelected) {
                                setEmailPrefFilter(emailPrefFilter.filter(p => p !== pref));
                              } else {
                                setEmailPrefFilter([...emailPrefFilter, pref]);
                              }
                            }}
                          >
                            <div className="flex items-center justify-center w-4 h-4 border rounded">
                              {isSelected && <Check className="h-3 w-3" />}
                            </div>
                            <span className="text-sm">{displayLabel}</span>
                          </div>
                        );
                      })}
                    </div>
                  </PopoverContent>
                </Popover>

                {/* Reset button */}
                {(searchTerm || emailPrefFilter.length > 0) && (
                  <Button
                    variant="ghost"
                    onClick={() => {
                      setSearchTerm('');
                      setEmailPrefFilter([]);
                    }}
                    size="sm"
                    className="h-9"
                  >
                    Reset
                    <X className="ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>

              {/* Search */}
              <div className="relative sm:w-72">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, ABN..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9"
                />
              </div>

              {/* View Columns Button */}
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" size="sm" className="h-9">
                    <Eye className="h-4 w-4" />
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-56 p-2" align="end">
                  <div className="space-y-1">
                    <div className="text-sm font-medium mb-2 px-2">Toggle columns</div>
                    {Object.entries({
                      name: 'Company Name',
                      email: 'Primary Email',
                      email_pref: 'Email Pref',
                      status: 'Status',
                      rfq_count: 'RFQ Count',
                      invited_rfqs: 'Invited RFQs',
                      created: 'Created'
                    }).map(([key, label]) => (
                      <div
                        key={key}
                        className="flex items-center space-x-2 px-2 py-1.5 hover:bg-accent rounded cursor-pointer"
                        onClick={() => toggleColumn(key)}
                      >
                        <div className="flex items-center justify-center w-4 h-4 border rounded">
                          {visibleColumns[key] && <Check className="h-3 w-3" />}
                        </div>
                        <span className="text-sm">{label}</span>
                      </div>
                    ))}
                  </div>
                </PopoverContent>
              </Popover>
            </div>

            {/* Active Filter Badges */}
            {(searchTerm || emailPrefFilter.length > 0) && (
              <div className="flex flex-wrap gap-2">
                {searchTerm && (
                  <Badge variant="secondary" className="pl-2 pr-1">
                    Search: {searchTerm}
                    <X className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive" onClick={() => setSearchTerm('')} />
                  </Badge>
                )}
                {emailPrefFilter.map(pref => (
                  <Badge key={pref} variant="secondary" className="pl-2 pr-1">
                    {pref === 'No_email' ? 'No Email' : pref}
                    <X
                      className="h-3 w-3 ml-1.5 cursor-pointer hover:text-destructive"
                      onClick={() => setEmailPrefFilter(emailPrefFilter.filter(p => p !== pref))}
                    />
                  </Badge>
                ))}
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Desktop: Table View */}
          <div className="hidden md:block overflow-x-auto px-4">
            <Table>
            <TableHeader>
              <TableRow>
                {visibleColumns.name && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('name')}
                  >
                    <div className="flex items-center gap-1">
                      Company Name
                      {getSortIcon('name')}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.email && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('primary_procurement_email')}
                  >
                    <div className="flex items-center gap-1">
                      Primary Email
                      {getSortIcon('primary_procurement_email')}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.email_pref && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('email_preference')}
                  >
                    <div className="flex items-center gap-1">
                      Email Pref
                      {getSortIcon('email_preference')}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.status && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center gap-1">
                      Status
                      {getSortIcon('status')}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.rfq_count && (
                  <TableHead
                    className="text-center cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('rfq_count')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      RFQ Count
                      {getSortIcon('rfq_count')}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.invited_rfqs && (
                  <TableHead
                    className="text-center cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('invited_rfqs_count')}
                  >
                    <div className="flex items-center justify-center gap-1">
                      Invited RFQs
                      {getSortIcon('invited_rfqs_count')}
                    </div>
                  </TableHead>
                )}
                {visibleColumns.created && (
                  <TableHead
                    className="cursor-pointer hover:bg-accent"
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="flex items-center gap-1">
                      Created
                      {getSortIcon('created_at')}
                    </div>
                  </TableHead>
                )}
              </TableRow>
            </TableHeader>
            <TableBody>
              {clients.map((client) => (
                <TableRow
                  key={client.id}
                  className="cursor-pointer hover:bg-gray-50"
                  onClick={() => navigate(`/recruiter/clients/${client.id}`)}
                >
                  {visibleColumns.name && (
                    <TableCell className="font-medium">{client.name}</TableCell>
                  )}
                  {visibleColumns.email && (
                    <TableCell>{client.primary_procurement_email || 'Not provided'}</TableCell>
                  )}
                  {visibleColumns.email_pref && (
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={
                          client.email_preference === 'Send'
                            ? 'bg-green-50 text-green-700 border-green-200'
                            : client.email_preference === 'No_email'
                            ? 'bg-slate-50 text-slate-500 border-slate-200'
                            : 'bg-blue-50 text-blue-700 border-blue-200'
                        }
                      >
                        {client.email_preference === 'No_email' ? 'No Email' : (client.email_preference || 'Draft')}
                      </Badge>
                    </TableCell>
                  )}
                  {visibleColumns.status && (
                    <TableCell>
                      <Badge
                        className={
                          client.status === 'Active'
                            ? 'bg-green-100 text-green-800'
                            : client.status === 'Pending'
                            ? 'bg-yellow-100 text-yellow-800'
                            : 'bg-gray-100 text-gray-800'
                        }
                      >
                        {client.status || 'Active'}
                      </Badge>
                    </TableCell>
                  )}
                  {visibleColumns.rfq_count && (
                    <TableCell className="text-center">
                      <Badge variant="secondary">
                        {client.rfq_count || 0}
                      </Badge>
                    </TableCell>
                  )}
                  {visibleColumns.invited_rfqs && (
                    <TableCell className="text-center">
                      <Badge variant={client.invited_rfqs_count > 0 ? 'default' : 'outline'}>
                        {client.invited_rfqs_count || 0}
                      </Badge>
                    </TableCell>
                  )}
                  {visibleColumns.created && (
                    <TableCell className="text-sm text-gray-600">
                      {formatDate(client.created_at, { style: 'short', fallback: 'N/A' })}
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          </div>

          {/* Mobile: Card View */}
          <div className="md:hidden space-y-3 p-2 w-full max-w-full overflow-hidden">
            {clients.map((client) => (
              <MobileTableCard
                key={client.id}
                item={client}
                primaryField="name"
                secondaryField="primary_procurement_email"
                statusField="status"
                getStatusVariant={(status) => {
                  const colors = {
                    'Active': 'default',
                    'Pending': 'secondary',
                  };
                  return colors[status] || 'outline';
                }}
                fields={[
                  {
                    label: 'Email Pref',
                    key: 'email_preference',
                    render: (pref) => (
                      <Badge
                        variant="outline"
                        className={
                          pref === 'Send'
                            ? 'bg-green-50 text-green-700 border-green-200 text-xs'
                            : pref === 'No_email'
                            ? 'bg-slate-50 text-slate-500 border-slate-200 text-xs'
                            : 'bg-blue-50 text-blue-700 border-blue-200 text-xs'
                        }
                      >
                        {pref === 'No_email' ? 'No Email' : (pref || 'Draft')}
                      </Badge>
                    ),
                    showOnMobile: true,
                  },
                  {
                    label: 'RFQs',
                    key: 'rfq_count',
                    render: (count) => `${count || 0} total`,
                    showOnMobile: true,
                  },
                  {
                    label: 'Invited RFQs',
                    key: 'invited_rfqs_count',
                    render: (count) => (
                      <Badge variant={count > 0 ? 'default' : 'outline'} className="text-xs">
                        {count || 0}
                      </Badge>
                    ),
                    showOnMobile: true,
                  },
                  {
                    label: 'Created',
                    key: 'created_at',
                    render: (date) => formatDate(date, { style: 'short', fallback: 'N/A' }),
                    showOnMobile: true,
                  },
                ]}
                actions={
                  <Button
                    size="sm"
                    variant="outline"
                    className="flex-1 h-8 text-xs"
                    onClick={() => navigate(`/recruiter/clients/${client.id}`)}
                  >
                    View Details
                  </Button>
                }
                onClick={() => navigate(`/recruiter/clients/${client.id}`)}
              />
            ))}
          </div>

          {clients.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500">No clients found</p>
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="text-indigo-600 hover:text-indigo-800 text-sm mt-2"
                >
                  Clear search
                </button>
              )}
            </div>
          )}

          {/* Pagination Controls */}
          {totalItems > 0 && (
            <div className="mt-4 flex flex-col sm:flex-row items-center justify-between gap-4 border-t pt-4 px-4 sm:px-0">
              {/* Items per page selector */}
              <div className="flex items-center gap-2 text-sm">
                <span className="text-muted-foreground">Show</span>
                <select
                  value={itemsPerPage}
                  onChange={handleItemsPerPageChange}
                  className="border rounded px-2 py-1 bg-background"
                >
                  <option value={15}>15</option>
                  <option value={30}>30</option>
                  <option value={45}>45</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-muted-foreground">per page</span>
              </div>

              {/* Page info */}
              <div className="text-sm text-muted-foreground">
                Showing {Math.min((currentPage - 1) * itemsPerPage + 1, totalItems)} to{' '}
                {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}
              </div>

              {/* Page navigation */}
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                >
                  <ChevronLeft className="h-4 w-4" />
                  Previous
                </Button>

                {/* Page numbers */}
                <div className="flex items-center gap-1">
                  {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                    let pageNum;
                    if (totalPages <= 5) {
                      pageNum = i + 1;
                    } else if (currentPage <= 3) {
                      pageNum = i + 1;
                    } else if (currentPage >= totalPages - 2) {
                      pageNum = totalPages - 4 + i;
                    } else {
                      pageNum = currentPage - 2 + i;
                    }

                    return (
                      <Button
                        key={pageNum}
                        variant={currentPage === pageNum ? 'default' : 'outline'}
                        size="sm"
                        onClick={() => handlePageChange(pageNum)}
                        className="min-w-[2.5rem]"
                      >
                        {pageNum}
                      </Button>
                    );
                  })}
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Client Modal */}
      <AddClientModal
        open={addModalOpen}
        onOpenChange={setAddModalOpen}
        onSuccess={() => {
          refetch();
          setAddModalOpen(false);
        }}
      />
    </ListContainer>
  );
};

export default ClientsList;
